// src/features/fb/cookies/questions/CookiesQuestions.jsx
import React, { useState, useRef } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import BatchSizeQuestion from "./BatchSizeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import PartDetailsQuestion from "./PartDetailsQuestion";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {object} initialData - Initial data to populate the form
 */
export default function LaminatesQuestions({ onComplete, onDataChange, initialData }) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        measurementBasis: "repeat",
        pouchLength: "",
        pouchWidth: "",
        sealAllowance: "",
        gusset: "",
        partLength: "",
        partWidth: "",
        wastagePct: "",
      },
      partDetails: {
        regrindPct: "",
        avgWallThickness: "",
        burstingFactor: "",
        layers: [],
        printing: "",
        numberOfColors: "",
        printingCoverage: "",
        inkCoverage: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 4; // Last question
    if (initialData.partDetails) return 3; // Part Details
    if (initialData.partWeightDimensions) return 2;
    if (initialData.batchSize) return 1;
    if (initialData.materialSelection) return 0;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  const [editingLayerIndex, setEditingLayerIndex] = useState(null); // Track which layer is being edited
  const [demoMode, setDemoMode] = useState(false);
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor = theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";
  
  // Refs for validation
  const partDetailsRef = useRef(null);
  
  // Check if all questions are answered
  const isPouchBasis =
    productFormData.partWeightDimensions?.measurementBasis === "pouch";

  const isDimensionsAnswered = isPouchBasis
    ? productFormData.partWeightDimensions?.pouchLength &&
      productFormData.partWeightDimensions?.pouchWidth &&
      productFormData.partWeightDimensions?.sealAllowance !== "" &&
      productFormData.partWeightDimensions?.gusset !== ""
    : productFormData.partWeightDimensions?.partLength &&
      productFormData.partWeightDimensions?.partWidth &&
      productFormData.partWeightDimensions?.wastagePct !== "";

  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.batchSize &&
    parseFloat(productFormData.batchSize) > 0 &&
    isDimensionsAnswered &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
    // Don't call onDataChange(null) when incomplete - this would wipe parent state
    // Parent should retain the last valid data for persistence across navigation
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display - only show values that have been answered
  // On double-click of a layer row, populate the form fields for editing
  const handleLayerRowDoubleClick = (layer, index) => {
    setEditingLayerIndex(index); // Set the layer index being edited
    setProductFormData((prev) => ({
      ...prev,
      partDetails: {
        ...(prev.partDetails || {}),
        avgWallThickness: layer?.filmStructure || "",
        burstingFactor: layer?.microns || "",
      },
    }));
  };

  const dimensionsSelectedValue = (() => {
    const dims = productFormData.partWeightDimensions || {};
    const basis = dims.measurementBasis || "repeat";

    if (basis === "pouch") {
      if (
        dims.pouchLength &&
        dims.pouchWidth &&
        dims.sealAllowance !== "" &&
        dims.gusset !== ""
      ) {
        return `Basis: Pouch/Sachet, Pouch L: ${dims.pouchLength}mm, Pouch W: ${dims.pouchWidth}mm, Seal allowance: ${dims.sealAllowance}mm, Gusset: ${dims.gusset}mm`;
      }
      return null;
    }

    if (
      dims.partLength &&
      dims.partWidth &&
      dims.wastagePct !== ""
    ) {
      return `Basis: Repeat x Coil width, Repeat: ${dims.partLength}mm, Coil W: ${dims.partWidth}mm, Wastage: ${dims.wastagePct}%`;
    }

    return null;
  })();

  const productSelectedValues = [
    productFormData.productName && {
      label: "Part Name:",
      value: productFormData.productName,
    },
    productFormData.batchSize && {
      label: "Batch Size:",
      value: productFormData.batchSize,
    },
    // Show Dimensions for either basis after step 2 is answered
    dimensionsSelectedValue && {
        label: "Dimensions:",
        value: dimensionsSelectedValue,
      },
    // Show Layers Table if any layers exist
    ...(productFormData.partDetails?.layers && productFormData.partDetails.layers.length > 0
      ? [{
          label: "Layers:",
          value: (
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: "transparent",
                backgroundImage: "none",
                boxShadow: "none",
                border: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                borderRadius: 1.5,
                mt: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: perlColors.textSecondary,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                        py: 1,
                      }}
                    >
                      Layers
                    </TableCell>
                    <TableCell
                      sx={{
                        color: perlColors.textSecondary,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                        py: 1,
                      }}
                    >
                      Structures
                    </TableCell>
                    <TableCell
                      sx={{
                        color: perlColors.textSecondary,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                        py: 1,
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        color: perlColors.textSecondary,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                        py: 1,
                      }}
                    >
                      Microns
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productFormData.partDetails.layers.map((layer, index) => (
                    <TableRow
                      key={index}
                      onDoubleClick={() => handleLayerRowDoubleClick(layer, index)}
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(brandColor, 0.05),
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontSize: "0.875rem",
                          borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                          py: 1,
                        }}
                      >
                        {layer.layerName}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontSize: "0.875rem",
                          borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                          py: 1,
                        }}
                      >
                        {layer.filmStructure}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontSize: "0.875rem",
                          borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                          py: 1,
                        }}
                      >
                        {layer.type || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontSize: "0.875rem",
                          borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                          py: 1,
                        }}
                      >
                        {layer.microns}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ),
        }]
      : []),
    // Show Part Details if any field has a value (excluding printing details and layers)
    (productFormData.partDetails?.regrindPct) && {
        label: "Part Details:",
        value: [
          productFormData.partDetails?.regrindPct && `Regrind: ${productFormData.partDetails.regrindPct}%`,
        ]
          .filter(Boolean)
          .join(", "),
      },
    productFormData.manufacturingCountry && {
      label: "Manufacturing Country:",
      value: productFormData.manufacturingCountry,
    },
  ].filter(Boolean); // Remove falsy values

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Batch Size
        return (
          !productFormData.batchSize ||
          productFormData.batchSize.trim() === "" ||
          parseFloat(productFormData.batchSize) <= 0
        );
      case 2: // Part Weight and Dimensions
        if (productFormData.partWeightDimensions?.measurementBasis === "pouch") {
          return (
            !productFormData.partWeightDimensions?.pouchLength ||
            !productFormData.partWeightDimensions?.pouchWidth ||
            productFormData.partWeightDimensions?.sealAllowance === "" ||
            productFormData.partWeightDimensions?.gusset === ""
          );
        }
        return (
          !productFormData.partWeightDimensions?.partLength ||
          !productFormData.partWeightDimensions?.partWidth ||
          productFormData.partWeightDimensions?.wastagePct === ""
        );
      case 3: // Part Details
        // Check if at least one layer has been added
        const partDetails = productFormData.partDetails || {};
        if (!partDetails.layers || partDetails.layers.length === 0) return true;
        return false;
      case 4: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before moving to next step
  const handleStepChange = async (newStep) => {
    // Validation passed or not needed, proceed to next step
    setProductChatStep(newStep);
  };
  
  // Helper function to handle automatic navigation to next step on Enter key
  const handleNextStep = async (stepIndex) => {
    // Use handleStepChange to ensure consistent validation logic
    const nextStep = stepIndex + 1;
    const totalSteps = 5; // Total number of questions (printing removed)
    
    if (nextStep < totalSteps) {
      // Use handleStepChange which handles validation
      await handleStepChange(nextStep);
    } else {
      handleComplete();
    }
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        measurementBasis: "repeat",
        pouchLength: "",
        pouchWidth: "",
        sealAllowance: "",
        gusset: "",
        partLength: "",
        partWidth: "",
        wastagePct: "",
      },
      partDetails: {
        regrindPct: "",
        avgWallThickness: "",
        burstingFactor: "",
        layers: [],
        printing: "",
        numberOfColors: "",
        printingCoverage: "",
        inkCoverage: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setProductChatStep(0);
    setDemoMode(false); // Reset demo toggle button
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "Detergent pouch",
        materialSelection: "",
        batchSize: "9000",
        partWeightDimensions: {
          measurementBasis: "pouch",
          pouchLength: "60",
          pouchWidth: "40",
          sealAllowance: "8",
          gusset: "0",
          partLength: "",
          partWidth: "",
          wastagePct: "",
        },
        partDetails: {
          regrindPct: "",
          avgWallThickness: "",
          burstingFactor: "",
          layers: [
            {
              layerName: "Layer 1",
              filmStructure: "Met BOPP",
              type: "film",
              microns: "20",
            },
            {
              layerName: "Layer 2",
              filmStructure: "LDPE",
              type: "film",
              microns: "50",
            },
            {
              layerName: "Layer 3",
              filmStructure: "Solvent-based gravure ink",
              type: "ink",
              microns: "1.5",
            },
            {
              layerName: "Layer 4",
              filmStructure: "PU solvent-based lamination adhesive",
              type: "ink",
              microns: "3",
            },
          ],
          printing: "",
          numberOfColors: "",
          printingCoverage: "",
          inkCoverage: "",
        },
        marketPositioning: "",
        skuWeight: "",
        skuWeightUnit: "g",
        annualVolume: "",
        manufacturingCountry: "China",
      });
      // Navigate to the last step (index 4 = ManufacturingCountrySelect) to show the last question
      setProductChatStep(4);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
      />
      <BatchSizeQuestion
        value={productFormData.batchSize}
        onChange={(value) => setProductFormData({ ...productFormData, batchSize: value })}
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partWeightDimensions: value })
        }
      />
      <PartDetailsQuestion
        ref={partDetailsRef}
        value={productFormData.partDetails}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partDetails: value })
        }
        editingLayerIndex={editingLayerIndex}
        onEditComplete={() => setEditingLayerIndex(null)}
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
